import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface GoogleDriveIframeProps {
  src: string;
  onError: () => void;
  onLoad: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onSeek?: (time: number) => void;
  onVolumeChange?: (volume: number) => void;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

export interface GoogleDriveIframeRef {
  play: () => void;
  pause: () => void;
  seekTo: (time: number) => void;
  setVolume: (volume: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  isPlaying: () => boolean;
  toggleFullscreen: () => void;
}

const GoogleDriveIframe = forwardRef<GoogleDriveIframeRef, GoogleDriveIframeProps>(
  ({ src, onError, onLoad, onPlay, onPause, onSeek, onVolumeChange, onFullscreenChange }, ref) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const contentCheckRef = useRef<NodeJS.Timeout | null>(null);

    const maxRetries = 3;
    const loadTimeout = 10000; // 10 秒超時

    // 🚀 突破性策略：Google Drive 原生播放器直接控制
    const tryControlPlayback = (iframe: HTMLIFrameElement, fileId: string) => {
      // 調試日誌已移除
      
      try {
        // 方法 1: 使用 postMessage 發送播放指令
        const possibleOrigins = [
          'https://drive.google.com',
          'https://drive.usercontent.google.com',
          '*'
        ];
        
        possibleOrigins.forEach(origin => {
          try {
            iframe.contentWindow?.postMessage(
              { type: 'play', action: 'play' },
              origin
            );
            // 調試日誌已移除
          } catch (error) {
            console.log(`⚠️ 無法發送到 origin ${origin}:`, error);
          }
        });
        
        // 方法 2: 嘗試點擊 iframe 內的播放按鈕
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            const playButton = iframeDoc.querySelector('[aria-label="播放"], [aria-label="Play"], .play-button, .ytp-play-button');
            if (playButton) {
              (playButton as HTMLElement).click();
              // 調試日誌已移除
            }
          }
        } catch (error) {
          console.log('ℹ️ 跨域限制，無法直接操作 iframe 內容');
        }
        
        // 方法 3: 嘗試使用鍵盤快捷鍵
        possibleOrigins.forEach(origin => {
          try {
            iframe.contentWindow?.postMessage(
              { type: 'keydown', key: ' ' }, // 空格鍵播放/暫停
              origin
            );
          } catch (error) {
            // 忽略錯誤
          }
        });
        
      } catch (error) {
        console.log('⚠️ 播放控制失敗:', error);
      }
    };

    // 嘗試多種播放控制方法
    const tryMultiplePlaybackMethods = (iframe: HTMLIFrameElement) => {
      // 調試日誌已移除
      
      try {
        const currentSrc = iframe.src;
        if (currentSrc.includes('preview')) {
          const fileId = currentSrc.match(/\/file\/d\/([^\/]+)/)?.[1];
          if (fileId) {
            // 策略 1: 嘗試使用 Google Drive 的 stream 模式
            const streamUrl = `https://drive.google.com/uc?export=stream&id=${fileId}`;
            // 調試日誌已移除
            
            // 創建一個隱藏的測試 iframe 來測試 stream 模式
            const testIframe = document.createElement('iframe');
            testIframe.src = streamUrl;
            testIframe.style.display = 'none';
            testIframe.style.width = '100%';
            testIframe.style.height = '100%';
            document.body.appendChild(testIframe);
            
            // 延遲後測試策略 2
            setTimeout(() => {
              document.body.removeChild(testIframe);
              
              // 策略 2: 嘗試使用 Google Drive 的 direct 模式
              const directUrl = `https://drive.google.com/uc?export=direct&id=${fileId}`;
              // 調試日誌已移除
              
              const testIframe2 = document.createElement('iframe');
              testIframe2.src = directUrl;
              testIframe2.style.display = 'none';
              testIframe2.style.width = '100%';
              testIframe2.style.height = '100%';
              document.body.appendChild(testIframe2);
              
              // 延遲後清理
              setTimeout(() => {
                document.body.removeChild(testIframe2);
              }, 2000);
            }, 2000);
          }
        }
      } catch (error) {
        console.log('⚠️ 無法測試播放策略:', error);
      }
    };

    // 回退到傳統方法
    const fallbackPlaybackControl = (iframe: HTMLIFrameElement | null) => {
      if (!iframe) return;
      
      // 調試日誌已移除
      
      try {
        // 嘗試使用 postMessage 控制
        const possibleOrigins = [
          'https://drive.google.com',
          'https://drive.usercontent.google.com',
          '*'
        ];
        
        possibleOrigins.forEach(origin => {
          try {
            iframe.contentWindow?.postMessage(
              { type: 'play', action: 'play' },
              origin
            );
            // 調試日誌已移除
          } catch (error) {
            console.log(`⚠️ 無法發送到 origin ${origin}:`, error);
          }
        });
        
      } catch (error) {
        console.log('⚠️ 傳統播放控制方法也失敗:', error);
      }
    };

    // 暴露方法給父組件
    useImperativeHandle(ref, () => ({
      play: () => {
        if (iframeRef.current) {
          try {
            // 🚀 突破性策略：Google Drive 原生播放器直接控制
            const iframe = iframeRef.current;
            // 調試日誌已移除
            
            // 策略 1: 強制切換到可控制的播放模式
            const currentSrc = iframe.src;
            if (currentSrc.includes('preview')) {
              const fileId = currentSrc.match(/\/file\/d\/([^\/]+)/)?.[1];
              if (fileId) {
                // 調試日誌已移除
                iframe.src = `https://drive.google.com/file/d/${fileId}/view?usp=sharing`;
                
                // 等待 iframe 載入完成後嘗試控制
                setTimeout(() => {
                  tryControlPlayback(iframe, fileId);
                }, 2000);
              }
            } else {
              // 如果已經是其他模式，直接嘗試控制
              const fileId = currentSrc.match(/\/file\/d\/([^\/]+)/)?.[1];
              if (fileId) {
                tryControlPlayback(iframe, fileId);
              }
            }
            
            // 策略 2: 嘗試多種播放控制方法
            tryMultiplePlaybackMethods(iframe);
            
          } catch (error) {
            console.log('⚠️ 突破性播放策略失敗:', error);
            // 回退到傳統方法
            fallbackPlaybackControl(iframeRef.current);
          }
        }
      },

      pause: () => {
        if (iframeRef.current) {
          try {
            const iframe = iframeRef.current;
            console.log('⏸️ 嘗試暫停 Google Drive 播放器');
            
            // 使用 postMessage 發送暫停指令
            const possibleOrigins = [
              'https://drive.google.com',
              'https://drive.usercontent.google.com',
              '*'
            ];
            
            possibleOrigins.forEach(origin => {
              try {
                iframe.contentWindow?.postMessage(
                  { type: 'pause', action: 'pause' },
                  origin
                );
                console.log(`⏸️ 已發送暫停指令到 origin: ${origin}`);
              } catch (error) {
                console.log(`⚠️ 無法發送到 origin ${origin}:`, error);
              }
            });
            
            // 嘗試點擊暫停按鈕
            try {
              const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
              if (iframeDoc) {
                const pauseButton = iframeDoc.querySelector('[aria-label="暫停"], [aria-label="Pause"], .pause-button, .ytp-pause-button');
                if (pauseButton) {
                  (pauseButton as HTMLElement).click();
                  // 調試日誌已移除
                }
              }
            } catch (error) {
              console.log('ℹ️ 跨域限制，無法直接操作 iframe 內容');
            }
            
            setIsPlaying(false);
            onPause?.();
          } catch (error) {
            console.log('⚠️ 暫停控制失敗:', error);
          }
        }
      },

      seekTo: (time: number) => {
        if (iframeRef.current) {
          try {
            const iframe = iframeRef.current;
            console.log('⏩ 嘗試跳轉到時間:', time);
            
            // 使用 postMessage 發送跳轉指令
            const possibleOrigins = [
              'https://drive.google.com',
              'https://drive.usercontent.google.com',
              '*'
            ];
            
            possibleOrigins.forEach(origin => {
              try {
                iframe.contentWindow?.postMessage(
                  { type: 'seek', action: 'seek', time: time },
                  origin
                );
                console.log(`⏩ 已發送跳轉指令到 origin: ${origin}`);
              } catch (error) {
                console.log(`⚠️ 無法發送到 origin ${origin}:`, error);
              }
            });
            
            setCurrentTime(time);
            onSeek?.(time);
          } catch (error) {
            console.log('⚠️ 跳轉控制失敗:', error);
          }
        }
      },

      setVolume: (newVolume: number) => {
        if (iframeRef.current) {
          try {
            const iframe = iframeRef.current;
            console.log('🔊 嘗試設置音量:', newVolume);
            
            // 使用 postMessage 發送音量設置指令
            const possibleOrigins = [
              'https://drive.google.com',
              'https://drive.usercontent.google.com',
              '*'
            ];
            
            possibleOrigins.forEach(origin => {
              try {
                iframe.contentWindow?.postMessage(
                  { type: 'volume', action: 'setVolume', volume: newVolume },
                  origin
                );
                console.log(`🔊 已發送音量設置指令到 origin: ${origin}`);
              } catch (error) {
                console.log(`⚠️ 無法發送到 origin ${origin}:`, error);
              }
            });
            
            setVolume(newVolume);
            onVolumeChange?.(newVolume);
          } catch (error) {
            console.log('⚠️ 音量控制失敗:', error);
          }
        }
      },

      getCurrentTime: () => {
        return currentTime;
      },

      getDuration: () => {
        return duration;
      },

      isPlaying: () => {
        return isPlaying;
      },

      toggleFullscreen: () => {
        if (iframeRef.current) {
          try {
            const iframe = iframeRef.current;
            // 調試日誌已移除
            
            // 使用 postMessage 發送全螢幕切換指令
            const possibleOrigins = [
              'https://drive.google.com',
              'https://drive.usercontent.google.com',
              '*'
            ];
            
            possibleOrigins.forEach(origin => {
              try {
                iframe.contentWindow?.postMessage(
                  { type: 'fullscreen', action: 'toggle' },
                  origin
                );
                // 調試日誌已移除
              } catch (error) {
                console.log(`⚠️ 無法發送到 origin ${origin}:`, error);
              }
            });
            
            setIsFullscreen(!isFullscreen);
            onFullscreenChange?.(!isFullscreen);
          } catch (error) {
            console.log('⚠️ 全螢幕控制失敗:', error);
          }
        }
      }
    }));

    // 處理 iframe 載入
    const handleLoad = () => {
      // 調試日誌已移除
      setIsLoading(false);
      setHasError(false);
      onLoad();
      
      // 開始檢測影片比例
      setTimeout(() => {
        try {
          // 調試日誌已移除
          
          // 由於跨域限制，無法直接檢測影片比例
          // 設置為自動適配模式
          console.log('ℹ️ 由於跨域限制，跳過自動樣式調整，讓 Google Drive 播放器使用預設樣式');
          
          // 設置為自動適配模式，讓 Google Drive 播放器自行處理比例
          // 調試日誌已移除
          
        } catch (error) {
          console.log('⚠️ 無法檢測影片比例:', error);
        }
      }, 1000);

      // 方法 6: Google Drive 權限修復 + 智能播放模式選擇
      setTimeout(() => {
        try {
          const currentSrc = iframeRef.current?.src;
          if (currentSrc && currentSrc.includes('preview')) {
            const fileId = currentSrc.match(/\/file\/d\/([^\/]+)/)?.[1];
            if (fileId) {
              // Google Drive 權限修復 + 智能播放模式選擇
              // 調試日誌已移除
              
              // 創建一個隱藏的 iframe 來測試不同的播放模式
              const testIframe = document.createElement('iframe');
              testIframe.style.display = 'none';
              testIframe.style.width = '100%';
              testIframe.style.height = '100%';
              testIframe.style.position = 'absolute';
              testIframe.style.top = '0';
              testIframe.style.left = '0';
              testIframe.style.zIndex = '1000';
              
              // 混合策略：先用 preview 模式顯示，再切換到可控制模式
              const displayModes = [
                // 模式 1: 使用 Google Drive 的 preview 播放器（最穩定，無權限問題，用於顯示）
                `https://drive.google.com/file/d/${fileId}/preview?usp=sharing`,
                
                // 模式 2: 使用 Google Drive 的 preview 播放器（無參數，最穩定，用於顯示）
                `https://drive.google.com/file/d/${fileId}/preview`,
                
                // 模式 3: 使用 Google Drive 的 preview 播放器（drivesdk 參數，較穩定，用於顯示）
                `https://drive.google.com/file/d/${fileId}/preview?usp=drivesdk`,
                
                // 模式 4: 使用 Google Drive 的 preview 播放器（resourcekey 參數，備用方案，用於顯示）
                `https://drive.google.com/file/d/${fileId}/preview?usp=drivesdk&resourcekey=`,
              ];

              // 可控制的播放模式（用於實際播放控制）
              const controllableModes = [
                // 嘗試使用 uc 模式（可能被 CSP 阻止，但值得一試）
                `https://drive.google.com/uc?id=${fileId}&export=view`,
                `https://drive.google.com/uc?id=${fileId}&export=stream`,
                // 備用：嘗試 view 模式（可能出現 403，但功能完整）
                `https://drive.google.com/file/d/${fileId}/view?usp=sharing`,
                `https://drive.google.com/file/d/${fileId}/view?usp=drivesdk`,
              ];

              // 智能切換策略：先用 display 模式顯示，再切換到 controllable 模式
              let currentDisplayModeIndex = 0;
              let currentControllableModeIndex = 0;
              let displayModeSuccessful = false;
              let controllableModeSuccessful = false;
              
              let currentModeIndex = 0;
              let successfulMode: string | null = null;
              
              // 嘗試可控制模式的函數
              const tryControllableMode = () => {
                if (currentControllableModeIndex < controllableModes.length) {
                  const modeUrl = controllableModes[currentControllableModeIndex];
                  // 調試日誌已移除
                  
                  // 創建新的測試 iframe 來測試可控制模式
                  const testControllableIframe = document.createElement('iframe');
                  testControllableIframe.style.position = 'absolute';
                  testControllableIframe.style.left = '-9999px';
                  testControllableIframe.style.top = '-9999px';
                  testControllableIframe.style.width = '1px';
                  testControllableIframe.style.height = '1px';
                  testControllableIframe.style.zIndex = '1000';
                  testControllableIframe.src = modeUrl;
                  
                  document.body.appendChild(testControllableIframe);
                  
                  // 監聽載入事件
                  testControllableIframe.onload = () => {
                    // 調試日誌已移除
                    
                    // 檢查是否包含錯誤
                    try {
                      const iframeDoc = testControllableIframe.contentDocument || (testControllableIframe.contentWindow?.document);
                      const bodyText = iframeDoc?.body?.textContent || '';
                      
                      if (bodyText && (bodyText.includes('Access denied') || bodyText.includes('404') || bodyText.includes('Forbidden') || bodyText.includes('Not Found'))) {
                        console.log(`⚠️ 可控制模式 ${currentControllableModeIndex + 1} 包含錯誤，嘗試下一個`);
                        currentControllableModeIndex++;
                        // 清理測試 iframe
                        if (testControllableIframe.parentNode) {
                          testControllableIframe.parentNode.removeChild(testControllableIframe);
                        }
                        tryControllableMode();
                      } else {
                        console.log(`🎉 可控制模式 ${currentControllableModeIndex + 1} 成功，切換主播放器`);
                        
                        // 將成功的可控制模式應用到主 iframe
                        if (iframeRef.current) {
                          iframeRef.current.src = modeUrl;
                        }
                        
                        // 清理測試 iframe
                        if (testControllableIframe.parentNode) {
                          testControllableIframe.parentNode.removeChild(testControllableIframe);
                        }
                        
                        // 顯示成功消息
                        // 調試日誌已移除
                        controllableModeSuccessful = true;
                      }
                    } catch (error) {
                      console.log(`⚠️ 無法檢查可控制模式 ${currentControllableModeIndex + 1} 內容，嘗試下一個`);
                      currentControllableModeIndex++;
                      // 清理測試 iframe
                      if (testControllableIframe.parentNode) {
                        testControllableIframe.parentNode.removeChild(testControllableIframe);
                      }
                      tryControllableMode();
                    }
                  };
                  
                  // 監聽錯誤事件
                  testControllableIframe.onerror = () => {
                    console.log(`⚠️ 可控制模式 ${currentControllableModeIndex + 1} 載入失敗，嘗試下一個`);
                    currentControllableModeIndex++;
                    // 清理測試 iframe
                    if (testControllableIframe.parentNode) {
                      testControllableIframe.parentNode.removeChild(testControllableIframe);
                    }
                    tryControllableMode();
                  };
                  
                  // 設置超時
                  setTimeout(() => {
                    if (testControllableIframe.src === modeUrl && !controllableModeSuccessful) {
                      // 調試日誌已移除
                      currentControllableModeIndex++;
                      // 清理測試 iframe
                      if (testControllableIframe.parentNode) {
                        testControllableIframe.parentNode.removeChild(testControllableIframe);
                      }
                      tryControllableMode();
                    }
                  }, 3000);
                } else {
                  console.log('⚠️ 所有可控制模式都試過了，無法切換');
                  console.log('ℹ️ 將繼續使用 preview 模式顯示（無法控制播放）');
                }
              };

              const tryNextMode = () => {
                if (currentModeIndex < displayModes.length) {
                  const modeUrl = displayModes[currentModeIndex];
                  // 調試日誌已移除
                  
                  // 設置測試 iframe 的 src
                  testIframe.src = modeUrl;
                  
                  // 監聽 iframe 載入事件
                  testIframe.onload = () => {
                    // 調試日誌已移除
                    
                    // 智能策略：先用 display 模式顯示，再嘗試切換到 controllable 模式
                    if (currentModeIndex <= 2) {
                      // 前 3 個模式（preview 相關）先用於顯示
                      console.log(`🎉 顯示模式 ${currentModeIndex + 1} 成功，先用於顯示`);
                      
                      // 將成功的顯示模式應用到主 iframe
                      if (iframeRef.current) {
                        iframeRef.current.src = modeUrl;
                      }
                      
                      // 移除測試 iframe
                      if (testIframe.parentNode) {
                        testIframe.parentNode.removeChild(testIframe);
                      }
                      
                      // 延遲 2 秒後嘗試切換到可控制模式
                      setTimeout(() => {
                        // 調試日誌已移除
                        tryControllableMode();
                      }, 2000);
                      
                      return;
                    }
                    
                    // 檢查 iframe 內容是否包含錯誤
                    try {
                      const iframeDoc = testIframe.contentDocument || (testIframe.contentWindow?.document);
                      const bodyText = iframeDoc?.body?.textContent || '';
                      
                      if (bodyText && (bodyText.includes('Access denied') || bodyText.includes('404') || bodyText.includes('Forbidden') || bodyText.includes('Not Found'))) {
                        console.log(`⚠️ 播放模式 ${currentModeIndex + 1} 包含錯誤，嘗試下一個`);
                        currentModeIndex++;
                        tryNextMode();
                      } else {
                        console.log(`🎉 播放模式 ${currentModeIndex + 1} 成功，切換主播放器`);
                        successfulMode = modeUrl;
                        
                        // 將成功的模式應用到主 iframe
                        if (iframeRef.current) {
                          iframeRef.current.src = modeUrl;
                        }
                        
                        // 移除測試 iframe
                        if (testIframe.parentNode) {
                          testIframe.parentNode.removeChild(testIframe);
                        }
                        
                        // 顯示成功消息
                        // 調試日誌已移除
                      }
                    } catch (error) {
                      console.log(`⚠️ 無法檢查播放模式 ${currentModeIndex + 1} 內容，嘗試下一個`);
                      currentModeIndex++;
                      tryNextMode();
                    }
                  };
                  
                  // 監聽 iframe 錯誤事件
                  testIframe.onerror = () => {
                    console.log(`⚠️ 播放模式 ${currentModeIndex + 1} 載入失敗，嘗試下一個`);
                    currentModeIndex++;
                    tryNextMode();
                  };
                  
                  // 設置超時，如果 3 秒內無法載入，嘗試下一個
                  setTimeout(() => {
                    if (testIframe.src === modeUrl && !successfulMode) {
                      // 調試日誌已移除
                      currentModeIndex++;
                      tryNextMode();
                    }
                  }, 3000);
                } else {
                  console.log('⚠️ 所有播放模式都試過了，無法播放');
                  
                  // 移除測試 iframe
                  if (testIframe.parentNode) {
                    testIframe.parentNode.removeChild(testIframe);
                  }
                  
                  // 嘗試最後的備用方案：使用 preview 模式
                  // 調試日誌已移除
                  if (iframeRef.current) {
                    iframeRef.current.src = `https://drive.google.com/file/d/${fileId}/preview?usp=sharing`;
                  }
                  
                  // 顯示權限修復建議
                  // 調試日誌已移除
                  console.log('1. 右鍵點擊檔案 → 選擇「分享」');
                  console.log('2. 點擊「變更為任何人都能查看」');
                  console.log('3. 或設定為「任何有連結的人都能查看」');
                  console.log('4. 確保「不允許下載」選項未勾選');
                  console.log('5. 檢查檔案大小：超過 100MB 的檔案可能有播放限制');
                }
              };
              
              // 將測試 iframe 添加到 iframe 的容器中
              const iframeContainer = iframeRef.current?.parentElement;
              if (iframeContainer) {
                iframeContainer.style.position = 'relative';
                iframeContainer.appendChild(testIframe);
                
                // 開始嘗試播放模式
                tryNextMode();
                
                // 90秒後自動清理（如果仍然無法播放）
                setTimeout(() => {
                  if (testIframe.parentNode) {
                    testIframe.parentNode.removeChild(testIframe);
                  }
                }, 90000);
              }
            }
          }
        } catch (error) {
          console.log('⚠️ 無法創建權限修復播放器:', error);
        }
      }, 1000);
    };

    // 處理 iframe 錯誤
    const handleError = () => {
      console.log('❌ Google Drive iframe 載入失敗');
      setHasError(true);
      setIsLoading(false);
      onError();
    };

    // 重試載入
    const handleRetry = () => {
      if (retryCount < maxRetries) {
        setRetryCount(prev => prev + 1);
        setHasError(false);
        setIsLoading(true);
        
        // 重新載入 iframe
        if (iframeRef.current) {
          iframeRef.current.src = src;
        }
      }
    };

    // 清理超時
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (contentCheckRef.current) {
          clearTimeout(contentCheckRef.current);
        }
      };
    }, []);

    // 設置載入超時
    useEffect(() => {
      if (isLoading) {
        timeoutRef.current = setTimeout(() => {
          // 調試日誌已移除
          handleError();
        }, loadTimeout);
      }
    }, [isLoading]);

    if (hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: '400px',
            bgcolor: 'background.paper',
            borderRadius: 1,
            p: 3
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Google Drive 影片載入失敗
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
            無法載入 Google Drive 影片，請檢查檔案權限設定
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            disabled={retryCount >= maxRetries}
            sx={{ mt: 2 }}
          >
            重試載入 ({retryCount}/{maxRetries})
          </Button>
          {retryCount >= maxRetries && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              已達最大重試次數，請檢查網路連線或檔案權限
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.paper',
              zIndex: 1
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
        <iframe
          ref={iframeRef}
          src={src}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '8px',
            display: 'block'
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={handleLoad}
          onError={handleError}
        />
      </Box>
    );
  }
);

GoogleDriveIframe.displayName = 'GoogleDriveIframe';

export default GoogleDriveIframe;
